import React from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import PmaEditor from "./PMAEditor";
import axios from "common/interceptors";
import {
  startPrintFontGate,
  stopPrintFontGate,
  signalPrintReadyIfIdle,
  CRITICAL_PDF_PRINT_FONTS,
} from "common/utils/pmaPrintReady";
import { isPdfPrintVersion } from "./CommonEditor/pdfPrintVersion";

// Wrapper component to use useParams hook
const PrintPMAWrapper = (props) => {
  const params = useParams();
  return <PrintPMA {...props} params={params} />;
};

class PrintPMA extends React.Component {
  state = {
    singleHOData: null,
    agentData: null,
    mlsAreas: null,
    MQCityDetails: null,
    currentArea: null,
    bubbleBoxes: [],
    version: "year-end-2022",
    coverPageDetails: null,
    quarterlyAreaTable: null,
    testimonials: null,

    salePriceForCities: null,
    yearEnd: null,
    qArea: null,
    qCity: null,
    missionStatement: null,
    aprilData: null,
    flipBook: false,
    agentFocusInfoList: null,
    designDetailsInfoList: null,
    tpTrends2WatchList: null,
    fontsReady: false,
    fontsTimedOut: false,
  };

  async componentDidMount() {
    const pdfPrintVersion = isPdfPrintVersion();
    if (pdfPrintVersion) {
      import("./CommonEditor/pdfprintFonts.scss");
      import("./CommonEditor/barcodeFont.scss");
    }
    startPrintFontGate(
      this,
      pdfPrintVersion ? { fontSpecs: CRITICAL_PDF_PRINT_FONTS } : undefined
    );
    // Get parameters from URL or props
    let mailProId, listgenUserID;

    if (this.props.params) {
      mailProId = this.props.params.mailproid;
      listgenUserID = this.props.params.listgenUserID;
    } else if (this.props.match && this.props.match.params) {
      mailProId = this.props.match.params.mailproid;
      listgenUserID = this.props.match.params.listgenUserID;
    } else {
      // Fallback: extract from URL
      const urlParams = new URLSearchParams(window.location.search);
      mailProId = urlParams.get("mailproid") || this.props.mailproid;
      listgenUserID =
        urlParams.get("listgenUserID") || this.props.listgenUserID;
    }

    const { version } = this.state;
    let url = window.location.href;
    if (url.includes("test-print")) {
      listgenUserID = url[url.length - 2];
    }

    // Validate required parameters
    if (!mailProId || !listgenUserID) {
      console.error("Missing required parameters: mailProId or listgenUserID");
      return;
    }

    try {
      // Parallel API calls for independent data
      const [mailPrintResponse, navBarResponse] = await Promise.all([
        axios.get(
          `/admin/getMailPrintInfoDetails?listgenUserID=${listgenUserID}&mailerProID=${mailProId}`,
          {
            headers: {
              "content-type": "application/json",
            },
            data: {},
          },
        ),
        axios.get(`/agent/getNavbarInfo?listgenUserID=${listgenUserID}`, {
          headers: {
            "content-type": "application/json",
          },
          data: {},
        }),
      ]);

      // Extract data from responses
      const singleHOData = mailPrintResponse.data.mailerProDTO;
      const agentData = mailPrintResponse.data.listgenUserDTO;
      const mlsAreas = navBarResponse.data;

      // Update state, then continue flow in the setState callback to avoid
      // reading stale state (setState is async)
      this.setState(
        {
          singleHOData,
          agentData,
          mlsAreas,
        },
        async () => {
          try {
            const date = new Date();
            const thisYear = date.getFullYear();
            const thisMonth = `${date.getMonth() + 1}`.padStart(2, 0);
            const today = `${date.getDate()}`.padStart(2, 0);

            const singleHODataLocal = this.state.singleHOData;
            const mlsAreasLocal = Array.isArray(this.state.mlsAreas)
              ? this.state.mlsAreas
              : [];

            // Safely derive the current area
            const thisArea =
              singleHODataLocal && singleHODataLocal.areaID
                ? singleHODataLocal.areaID
                : null;

            let currentArea = null;
            if (thisArea && mlsAreasLocal.length) {
              currentArea =
                mlsAreasLocal.find((area) => area.mlsAreaID === thisArea) ||
                mlsAreasLocal[0] ||
                null;
            } else if (mlsAreasLocal.length) {
              currentArea = mlsAreasLocal[0];
            }

            if (!currentArea) {
              console.warn("PrintPMA: No currentArea could be resolved");
              return;
            }

            const { mlsCityID, mlsAreaID } = currentArea;
            const subdivisionName =
              singleHODataLocal && singleHODataLocal.subdivisionName
                ? singleHODataLocal.subdivisionName
                : "-";

            if (currentArea) {
              let getPmaDetails = await axios
                .get(
                  `/agent/getPMADetails?areaCityPeriod=${mlsAreaID}-${mlsCityID}-${thisYear}-${thisMonth}-${today}&subDivision=${
                    subdivisionName ? subdivisionName : "-"
                  }&listgenUserID=${listgenUserID}&mailerProID=${mailProId}`,
                  {
                    headers: {
                      "content-type": "application/json",
                      "Access-Control-Allow-Origin": "*",
                    },
                    data: {},
                  },
                )
                .then((res) => {
                  let {
                    mailerPrintInfoDTO,
                    bpciList,
                    quadrantInfoList,
                    agentFocusInfoList,
                    designDetailsInfoList,
                    tpTrends2WatchList,
                    pmaQuarterlyCityDTO,
                    propertyPhotoDetailsDTO,
                    keyMarketAvgsList,
                    showMedian,
                    priceRangeDashBoardDetailsDTOArea,
                    priceRangeDashBoardDetailsDTOCity,
                  } = res.data;

                  const singleHOData = mailerPrintInfoDTO.mailerProDTO;
                  const agentData = mailerPrintInfoDTO.listgenUserDTO;
                  let dashboardValuation = res.data.dbpageFooterList
                    ? res.data.dbpageFooterList.filter(
                        (valuation) => valuation.dbpfareaID === mlsAreaID,
                      )[0]
                    : null;

                  // getNavbarInfo may not carry the alternate cover photo fields, so take them from this response
                  const detailsNavArea = (res.data.navbarInfoList || []).find(
                    (area) => area.mlsAreaID === mlsAreaID,
                  );
                  const currentAreaWithCoverPrefs = detailsNavArea
                    ? {
                        ...currentArea,
                        alternateCoverPhotoURL:
                          detailsNavArea.alternateCoverPhotoURL,
                        coverPhotoPreference: detailsNavArea.coverPhotoPreference,
                      }
                    : currentArea;

                  this.setState({
                    singleHOData,
                    agentData,
                    dashboardValuation,
                    altAgentData: agentData,
                    currentArea: currentAreaWithCoverPrefs,
                    missionStatement: res.data.missionStatementDTO,
                    quadrantDetails: quadrantInfoList,
                    agentFocusInfoList: agentFocusInfoList,
                    designDetailsInfoList: designDetailsInfoList,
                    tpTrends2WatchList: tpTrends2WatchList ?? null,
                    volunteerDetails: bpciList,
                    coverPageDetails: res.data.pmaCoverPhotoDTO,
                    quarterlyAreaTable: res.data.pmaQuarterlyAreaDTO,
                    quarterlyCityTable: res.data.pmaQuarterlyCityDTO,
                    aprilData: {
                      pmaQuarterlyAreaDTO: res.data.pmaQuarterlyAreaDTO,
                      pmaQuarterlyCityDTO: res.data.pmaQuarterlyCityDTO,
                    },
                    propertyPhotoDetailsDTO: propertyPhotoDetailsDTO,
                    bubbleBoxes: res.data.marketValueInfoList,
                    testimonials: res.data.lgTestmonialsDTO,
                    MQCityDetails: pmaQuarterlyCityDTO,
                    miniTableDetailsDTO: res.data.miniTableDetailsDTO,
                    keyMarketAvgsList: keyMarketAvgsList,
                    showMedian: showMedian,
                    priceRangeDashBoardDetailsDTOArea:
                      priceRangeDashBoardDetailsDTOArea,
                    priceRangeDashBoardDetailsDTOCity:
                      priceRangeDashBoardDetailsDTOCity,
                  });
                })
                .catch((err) =>
                  console.log("error retrieving table bubble data" + err),
                );
            }
          } catch (error) {
            console.log("error retrieving initial data", error);
            return; // Exit early if initial data fails
          }
        },
      ); // Close setState call
    } catch (error) {
      console.log("error in componentDidMount", error);
    }
  }
  isPrintDataReady = () => {
    const {
      version,
      currentArea,
      singleHOData,
      agentData,
      aprilData,
      coverPageDetails,
      bubbleBoxes,
    } = this.state;
    return !!(
      version === "year-end-2022" &&
      currentArea &&
      singleHOData &&
      agentData &&
      aprilData &&
      coverPageDetails &&
      bubbleBoxes &&
      Object.keys(bubbleBoxes).length >= 1
    );
  };

  componentDidUpdate() {
    signalPrintReadyIfIdle(this, this.isPrintDataReady());
  }

  componentWillUnmount() {
    stopPrintFontGate(this);
  }

  render() {
    const { propertyData } = this.props;
    const {
      singleHOData,
      agentData,
      altAgentData,
      mlsAreas,
      MQCityDetails,
      currentArea,
      version,
      quarterlyCityTable,
      quarterlyAreaTable,
      coverPageDetails,
      testimonials,

      salePriceForCities,
      yearEnd,
      qArea,
      qCity,
      aprilData,
      flipBook,
      missionStatement,
      propertyPhotoDetailsDTO,
      pmaQuarterlyCombinedCityDTO,
      quadrantDetails,
      volunteerDetails,
      bubbleBoxes,
      dashboardValuation,
      miniTableDetailsDTO,
      agentFocusInfoList,
      designDetailsInfoList,
      tpTrends2WatchList,
      keyMarketAvgsList,
      showMedian,
      priceRangeDashBoardDetailsDTOArea,
      priceRangeDashBoardDetailsDTOCity,
      fontsReady,
    } = this.state;

    //below, show the main data points that we need so we can make sure the page doesnt show until the data is there. You may need to edit ye2 for September's api by putting the variables we will be using in ye2
    if (this.isPrintDataReady() && fontsReady) {
      return (
        <div className="pma-creator">
          <main>
            <section>
              <PmaEditor
                fromEditor={false}
                pdfPrintVersion={isPdfPrintVersion()}
                propertyData={propertyData}
                printVersion={true}
                version={version}
                singleHOData={singleHOData}
                agentData={agentData}
                altAgentData={altAgentData}
                currentArea={currentArea}
                MQCityDetails={MQCityDetails}
                mlsAreas={mlsAreas}
                quarterlyAreaTable={quarterlyAreaTable}
                quarterlyCityTable={quarterlyCityTable}
                coverPageDetails={coverPageDetails}
                printTestimonials={testimonials}
                dashboardValuation={dashboardValuation}
                salePriceForCities={salePriceForCities}
                yearEnd={yearEnd}
                qArea={qArea}
                qCity={qCity}
                aprilData={aprilData}
                flipBook={flipBook}
                dontShowPma={false}
                missionStatement={missionStatement}
                propertyPhotoDetailsDTO={propertyPhotoDetailsDTO}
                pmaQuarterlyCombinedCityDTO={pmaQuarterlyCombinedCityDTO}
                quadrantDetails={quadrantDetails}
                agentFocusInfoList={agentFocusInfoList}
                designDetailsInfoList={designDetailsInfoList}
                tpTrends2WatchList={tpTrends2WatchList}
                volunteerDetails={volunteerDetails}
                bubbleBoxes={bubbleBoxes}
                miniTableDetailsDTO={miniTableDetailsDTO}
                keyMarketAvgsList={keyMarketAvgsList}
                showMedian={showMedian}
                priceRangeDashBoardDetailsDTOArea={
                  priceRangeDashBoardDetailsDTOArea
                }
                priceRangeDashBoardDetailsDTOCity={
                  priceRangeDashBoardDetailsDTOCity
                }
              />
            </section>
          </main>
        </div>
      );
    } else {
      return <div>Loading PMA...</div>;
    }
  }
}

PrintPMA.propTypes = {
  params: PropTypes.shape({
    mailproid: PropTypes.string,
    listgenUserID: PropTypes.string,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      mailproid: PropTypes.string,
      listgenUserID: PropTypes.string,
    }),
  }),
  mailproid: PropTypes.string,
  listgenUserID: PropTypes.string,
};

export default PrintPMAWrapper;
